import _ from 'lodash';
import { TAction } from '../../../../../shared/frontend/scripts/actions/types/action';
import {
  TApiKeyRequestKey,
  TApiRequestState,
  TApplicationRequestKey,
  TApplicationsManagementState
} from '../libs/store';

const getInitialState = (): TApplicationsManagementState => ({
  applications: {
    list: [],
    isLoaded: false,
    isLoading: false,
    error: null,
    maxCountOfApps: 0
  },
  details: {
    application: null,
    isLoadingApiKeys: false,
    isLoaded: false,
    isLoading: false,
    error: null,
    apiKeysRequests: {},
    requests: {}
  }
});

// Action Types
const REGENERATE_API_KEY = 'APPLICATIONS_MANAGEMENT/REGENERATE_API_KEY';
const GENERATE_API_KEY = 'APPLICATIONS_MANAGEMENT/GENERATE_API_KEY';
const CREATE_APPLICATION = 'APPLICATIONS_MANAGEMENT/CREATE_APPLICATION';
const TOGGLE_BAN_APPLICATION = 'APPLICATIONS_MANAGEMENT/TOGGLE_BAN_APPLICATION';
const GET_APPLICATIONS_LIST = 'APPLICATIONS_MANAGEMENT/GET_APPLICATIONS_LIST';
const GET_APPLICATION = 'APPLICATIONS_MANAGEMENT/GET_APPLICATION';
const EDIT_APPLICATION = 'APPLICATIONS_MANAGEMENT/EDIT_APPLICATION';
const CHANGE_RATE_LIMIT_API_KEY = 'APPLICATIONS_MANAGEMENT/CHANGE_RATE_LIMIT_API_KEY';
const SET_API_KEY_REQUEST = 'APPLICATIONS_MANAGEMENT/SET_API_KEY_REQUEST';
const SET_APPLICATION_REQUEST = 'APPLICATIONS_MANAGEMENT/SET_APPLICATION_REQUEST';

const SET_LOADING = 'APPLICATIONS_MANAGEMENT/SET_LOADING';
const SET_LOADING_API_KEYS = 'APPLICATIONS_MANAGEMENT/SET_LOADING_API_KEYS';
const SET_LOADED = 'APPLICATIONS_MANAGEMENT/SET_LOADED';
const SET_FAILURE = 'APPLICATIONS_MANAGEMENT/SET_FAILURE';

const LOAD_APPLICATIONS_LIST = 'APPLICATIONS_MANAGEMENT/LOAD_APPLICATIONS_LIST';
const LOAD_APPLICATION = 'APPLICATIONS_MANAGEMENT/LOAD_APPLICATION';
const LOAD_APPLICATION_API_KEYS_LIST = 'APPLICATIONS_MANAGEMENT/LOAD_APPLICATION_API_KEYS_LIST';

// Actions

const regenerateApiKey = (payload: { apiKeyId: number }) => {
  return {
    type: REGENERATE_API_KEY,
    payload
  };
};

const generateApiKey = (payload: any) => {
  return {
    type: GENERATE_API_KEY,
    payload
  };
};

const editApplication = (payload: any) => {
  return {
    type: EDIT_APPLICATION,
    payload
  };
};

const toggleBanApplication = (payload: { appId: number }) => {
  return {
    type: TOGGLE_BAN_APPLICATION,
    payload
  };
};

const changeRateLimitApiKey = (payload: { perMinuteRateLimit: number; apiKeyId: number }) => {
  return {
    type: CHANGE_RATE_LIMIT_API_KEY,
    payload
  };
};

const setLoading = (payload: { target: string; status: boolean }) => {
  return {
    type: SET_LOADING,
    payload
  };
};

const setLoadingApiKeys = (payload: boolean) => {
  return {
    type: SET_LOADING_API_KEYS,
    payload
  };
};

const setLoaded = (payload: { target: string; status: boolean }) => {
  return {
    type: SET_LOADED,
    payload
  };
};

const setFailure = (payload: { target: string; error: any }) => {
  return {
    type: SET_FAILURE,
    payload
  };
};

const loadApplicationsList = (payload: any) => {
  return {
    type: LOAD_APPLICATIONS_LIST,
    payload
  };
};

const loadApplication = (payload: Partial<TApplicationsManagementState['details']>) => {
  return {
    type: LOAD_APPLICATION,
    payload
  };
};

const loadApplicationApiKeysList = (payload: any) => {
  return {
    type: LOAD_APPLICATION_API_KEYS_LIST,
    payload
  };
};

const getApplicationsList = () => {
  return {
    type: GET_APPLICATIONS_LIST
  };
};

const getApplication = (payload: string) => {
  return {
    type: GET_APPLICATION,
    payload
  };
};

const createApplication = (payload: any) => {
  return {
    type: CREATE_APPLICATION,
    payload
  };
};

const setRequestPendingKey = (payload: {
  apiKeyId: number;
  request: Partial<TApiRequestState>;
  requestKey: TApiKeyRequestKey;
}) => {
  return {
    type: SET_API_KEY_REQUEST,
    payload
  };
};

const setApplicationRequest = (payload: { request: Partial<TApiRequestState>; requestKey: TApplicationRequestKey }) => {
  return {
    type: SET_APPLICATION_REQUEST,
    payload
  };
};

export default function reducer(state: any = getInitialState(), action: TAction<any>): any {
  switch (action.type) {
    case SET_LOADING: {
      const { target, status } = action.payload;
      return { ...state, [target]: { ...state[target], isLoading: status } };
    }
    case SET_LOADING_API_KEYS: {
      return { ...state, details: { ...state.details, isLoadingApiKeys: action.payload } };
    }
    case SET_LOADED: {
      const { target, status } = action.payload;
      return { ...state, [target]: { ...state[target], isLoaded: status } };
    }
    case SET_FAILURE: {
      const { target, error } = action.payload;
      return { ...state, [target]: { ...state[target], error } };
    }
    case SET_API_KEY_REQUEST: {
      const { requestKey, request, apiKeyId } = action.payload as {
        apiKeyId: number;
        request: Partial<TApiRequestState>;
        requestKey: TApiKeyRequestKey;
      };

      return {
        ...state,
        details: {
          ...state.details,
          apiKeysRequests: {
            ...state.details.apiKeysRequests,
            [apiKeyId]: {
              ..._.merge({}, state.details.apiKeysRequests[apiKeyId], {
                [requestKey]: _.merge({}, state.details.apiKeysRequests[apiKeyId]?.[requestKey], request)
              })
            }
          }
        }
      };
    }
    case SET_APPLICATION_REQUEST: {
      const { requestKey, request } = action.payload as {
        requestKey: TApplicationRequestKey;
        request: Partial<TApiRequestState>;
      };
      return {
        ...state,
        details: {
          ...state.details,
          requests: {
            ...state.details.requests,
            [requestKey]: _.merge({}, state.details.requests[requestKey], request)
          }
        }
      };
    }
    case LOAD_APPLICATIONS_LIST: {
      return {
        ...state,
        applications: {
          ...state.applications,
          ...action.payload,
          error: null
        }
      };
    }
    case LOAD_APPLICATION: {
      return {
        ...state,
        details: {
          ...state.details,
          ...action.payload,
          error: null
        }
      };
    }
    case LOAD_APPLICATION_API_KEYS_LIST: {
      return {
        ...state,
        details: {
          ...state.details,
          application: {
            ...state.details.application,
            ...action.payload
          },
          error: null
        }
      };
    }
    case CREATE_APPLICATION: {
      return {
        ...state,
        applications: {
          ...state.applications,
          list: [...state.applications.list, action.payload]
        },
        details: {
          ...state.details,
          application: {
            ...action.payload
          },
          error: null
        }
      };
    }
    case EDIT_APPLICATION: {
      const updatedApplicationList = state.applications.list.map((application: any) => {
        if (application.id === action.payload.id) {
          return { ...application, ...action.payload };
        }
        return application;
      });
      return {
        ...state,
        applications: {
          ...state.applications,
          list: updatedApplicationList
        },
        details: {
          ...state.details,
          application: {
            ...action.payload
          },
          error: null
        }
      };
    }
    default:
      return state;
  }
}

export const actions = {
  regenerateApiKey,
  generateApiKey,
  toggleBanApplication,
  editApplication,
  changeRateLimitApiKey,
  setRequestPendingKey,
  setLoading,
  setLoaded,
  setLoadingApiKeys,
  setFailure,
  getApplicationsList,
  getApplication,
  createApplication,
  // load action
  loadApplicationsList,
  loadApplicationApiKeysList,
  loadApplication,
  setApplicationRequest
};

export const actionTypes = {
  REGENERATE_API_KEY,
  GENERATE_API_KEY,
  TOGGLE_BAN_APPLICATION,
  EDIT_APPLICATION,
  CREATE_APPLICATION,
  SET_LOADING,
  SET_FAILURE,
  LOAD_APPLICATIONS_LIST,
  GET_APPLICATIONS_LIST,
  GET_APPLICATION,
  CHANGE_RATE_LIMIT_API_KEY
};
